import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Grid } from '../Grid/Grid.js'
import { ScaledGrid } from '../Grid/ScaledGrid.js'
import { ActionBar } from '../ActionBar/ActionBar.js'
import { SonarResult } from '../SonarResult/SonarResult.js'
import { useGameStore } from '../../store/gameStore.js'
import { useUIStore } from '../../store/uiStore.js'
import { useAnimationStore } from '../../store/animationStore.js'
import { useGame } from '../../hooks/useGame.js'
import { useWarning } from '../../hooks/useWarning.js'
import { useAudio } from '../../hooks/useAudio.js'
import { useTheater } from '../../hooks/useTheater.js'
import { useIsWide } from '../../hooks/useIsWide.js'
import { isWebGLAvailable } from '../../utils/webgl.js'

// Code-split the 3D path so 2D users never download three.js (TECH_REVIEW_v3_3D)
const Board3D = React.lazy(() =>
  import('../Scene3D/Board3D.js').then(m => ({ default: m.Board3D })),
)

function Board3DFallback() {
  return <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, backgroundColor: '#13315C' }} />
}
import type { Coord, CellState } from '@fleetduel/shared'
import { coordKey, getSonarZone } from '@fleetduel/shared'

export function Battle() {
  const {
    playerIndex,
    currentTurn,
    myShips,
    opponentBoard,
    myBoardHits,
    sunkenShips,
  } = useGameStore()

  const selectedAction = useUIStore(s => s.selectedAction)
  const sonarPreview = useUIStore(s => s.sonarPreview)
  const showWarning = useUIStore(s => s.showWarning)
  const warningMessage = useUIStore(s => s.warningMessage)
  const inputLocked = useAnimationStore(s => s.inputLocked)
  const isAnimating = useAnimationStore(s => s.current !== null)
  const skipAnimation = useAnimationStore(s => s.skip)

  const { performAction } = useGame()
  const { checkTorpedoWarning } = useWarning()
  const { play } = useAudio()
  const { theaterId, terrainCells, enemyTerrainCells, enemyBlockedCells, palette } = useTheater()
  const isWide = useIsWide()

  // Dual render path (TECH_REVIEW_v3_3D): 3D primary, 2D mandatory fallback
  const webgl = useMemo(() => isWebGLAvailable(), [])
  const [render3D, setRender3D] = useState(() => {
    if (!isWebGLAvailable()) return false
    try { return localStorage.getItem('fleetduel-render3d') !== 'false' } catch { return true }
  })
  const toggleRender3D = useCallback(() => {
    setRender3D(prev => {
      const next = !prev
      try { localStorage.setItem('fleetduel-render3d', String(next)) } catch {}
      return next
    })
  }, [])

  // Safety net: 3D FX (fx3d.tsx) resolve animations themselves, but if the
  // Board3D chunk is still loading no FX is mounted — force-resolve so the
  // game can never deadlock waiting on an animation.
  const currentAnimation = useAnimationStore(s => s.current)
  useEffect(() => {
    if (!render3D || !currentAnimation) return
    const timer = setTimeout(() => {
      if (useAnimationStore.getState().current === currentAnimation) {
        useAnimationStore.getState().resolve()
      }
    }, 5000)
    return () => clearTimeout(timer)
  }, [render3D, currentAnimation])

  const isMyTurn = currentTurn === playerIndex

  // Play turn-start sound when it becomes our turn
  const prevTurnRef = React.useRef(currentTurn)
  useEffect(() => {
    if (currentTurn !== prevTurnRef.current) {
      prevTurnRef.current = currentTurn
      if (isMyTurn) {
        play('turn-start', 'ui')
      }
    }
  }, [currentTurn, isMyTurn, play])

  const myBoardStates = useMemo(() => {
    const states = new Map<string, CellState>()
    for (const [key, hitState] of myBoardHits) {
      states.set(key, hitState)
    }
    return states
  }, [myBoardHits])

  const sonarZone = useMemo(() => {
    if (selectedAction !== 'sonar' || !sonarPreview) return undefined
    const zone = getSonarZone(sonarPreview)
    return new Set(zone.map(coordKey))
  }, [selectedAction, sonarPreview])

  const [pendingTarget, setPendingTarget] = React.useState<Coord | null>(null)

  const handleOpponentCellHover = useCallback((coord: Coord) => {
    if (selectedAction === 'sonar') {
      useUIStore.getState().setSonarPreview(coord)
    }
  }, [selectedAction])

  // Skip-ahead: clicking during animation skips it
  const handleGlobalClick = useCallback(() => {
    if (isAnimating) {
      skipAnimation()
    }
  }, [isAnimating, skipAnimation])

  const handleOpponentCellClick = useCallback((coord: Coord) => {
    // Block input during animation
    if (inputLocked) return
    if (!isMyTurn) return
    // Terrain cells cannot be targeted (GDD-v3 §4) — enemy board's layout
    if (enemyBlockedCells.has(coordKey(coord))) return

    if (selectedAction === 'sonar') {
      useUIStore.getState().setSonarPreview(coord)
    }

    if (selectedAction === 'torpedo') {
      const needsWarning = checkTorpedoWarning(coord)
      if (needsWarning) {
        setPendingTarget(coord)
        return
      }
    }

    performAction(coord)
  }, [isMyTurn, selectedAction, performAction, checkTorpedoWarning, inputLocked, enemyBlockedCells])

  const confirmWarning = useCallback(() => {
    if (pendingTarget) {
      performAction(pendingTarget)
      setPendingTarget(null)
      useUIStore.getState().hideWarning()
    }
  }, [pendingTarget, performAction])

  const cancelWarning = useCallback(() => {
    setPendingTarget(null)
    useUIStore.getState().hideWarning()
  }, [])

  // Fixed width so toggling between the two labels never reflows the layout
  const turnIndicator = (
    <div style={{
      fontSize: 14,
      fontWeight: 700,
      color: isMyTurn ? '#4fc3f7' : '#8DA9C4',
      padding: '8px 0',
      width: 180,
      whiteSpace: 'nowrap',
      backgroundColor: isMyTurn ? 'rgba(79, 195, 247, 0.1)' : 'transparent',
      borderRadius: 6,
      textAlign: 'center',
    }}>
      {isMyTurn ? 'YOUR TURN' : "OPPONENT'S TURN"}
    </div>
  )

  const sunkDisplay = sunkenShips.length > 0 ? (
    <div style={{ fontSize: 11, color: '#ef5350', textAlign: 'center', maxWidth: '92vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      Sunk: {sunkenShips.map(s => s.replace('_', ' ')).join(', ')}
    </div>
  ) : null

  // Boards are capped by viewport height (dvh: tracks the mobile URL bar) so
  // the whole battle always fits one screen without scrolling.
  // Wide: boards share the row with the 180px action column.
  // Narrow: the enemy board dominates (it is the only interactive board in
  // battle) and the own fleet is a small status minimap in the bottom row.
  const enemyWrapStyle: React.CSSProperties = isWide
    ? { width: 'min(620px, calc((100vw - 300px) / 2), calc(100dvh - 160px))' }
    : { width: 'min(92vw, 440px, max(200px, calc(100dvh - 240px)))' }
  const myWrapStyle: React.CSSProperties = isWide
    ? enemyWrapStyle
    : { width: 'min(30vw, 128px)', flexShrink: 0 }

  const enemyBoard = (
    <div style={enemyWrapStyle}>
      <div style={{ fontSize: 12, color: '#8DA9C4', marginBottom: 4, textAlign: 'center' }}>
        Enemy Waters
      </div>
      {render3D ? (
        <React.Suspense fallback={<Board3DFallback />}>
          <Board3D
            cellStates={opponentBoard}
            isOwnBoard={false}
            sonarZone={sonarZone}
            terrainCells={enemyTerrainCells}
            theaterId={theaterId}
            onCellClick={handleOpponentCellClick}
            onCellHover={handleOpponentCellHover}
          />
        </React.Suspense>
      ) : (
        <ScaledGrid>
          <Grid
            cellStates={opponentBoard}
            isOwnBoard={false}
            sonarZone={sonarZone}
            terrainCells={enemyTerrainCells}
            waterTint={palette.waterTint}
            onCellClick={handleOpponentCellClick}
            onCellHover={handleOpponentCellHover}
          />
        </ScaledGrid>
      )}
    </div>
  )

  const myBoard = (
    <div style={myWrapStyle}>
      <div style={{ fontSize: isWide ? 12 : 11, color: '#8DA9C4', marginBottom: 4, textAlign: 'center' }}>
        Your Fleet
      </div>
      {render3D ? (
        <React.Suspense fallback={<Board3DFallback />}>
          <Board3D
            cellStates={myBoardStates}
            isOwnBoard={true}
            ships={myShips}
            terrainCells={terrainCells}
            theaterId={theaterId}
          />
        </React.Suspense>
      ) : (
        <ScaledGrid>
          <Grid
            cellStates={myBoardStates}
            isOwnBoard={true}
            ships={myShips}
            terrainCells={terrainCells}
            waterTint={palette.waterTint}
          />
        </ScaledGrid>
      )}
    </div>
  )

  const renderToggle = webgl ? (
    <button
      onClick={toggleRender3D}
      style={{
        padding: '6px 14px',
        fontSize: 12,
        fontWeight: 700,
        border: '1px solid rgba(141, 169, 196, 0.4)',
        borderRadius: 6,
        backgroundColor: '#13315C',
        color: '#8DA9C4',
        cursor: 'pointer',
        letterSpacing: 1,
      }}
    >
      {render3D ? 'VIEW: 3D' : 'VIEW: 2D'}
    </button>
  ) : null

  const warningDialog = showWarning ? (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 200,
    }}>
      <div style={{
        backgroundColor: '#263238',
        padding: 24,
        borderRadius: 12,
        maxWidth: 300,
        textAlign: 'center',
      }}>
        <p style={{ color: '#ffb74d', fontSize: 14, marginBottom: 16 }}>
          {warningMessage}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={confirmWarning}
            style={{
              padding: '8px 20px',
              fontSize: 14,
              border: 'none',
              borderRadius: 6,
              backgroundColor: '#d32f2f',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Fire Anyway
          </button>
          <button
            onClick={cancelWarning}
            style={{
              padding: '8px 20px',
              fontSize: 14,
              border: 'none',
              borderRadius: 6,
              backgroundColor: '#37474f',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  ) : null

  if (isWide) {
    return (
      <div
        onClick={handleGlobalClick}
        className="screen-fill"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 24,
        }}>
          {enemyBoard}
          {/* Fixed width so changing content (sunk list) never shifts the boards */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            alignSelf: 'center',
            gap: 12,
            width: 180,
          }}>
            <ActionBar vertical />
            {sunkDisplay}
          </div>
          {myBoard}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          {turnIndicator}
          {renderToggle}
        </div>
        <SonarResult />
        {warningDialog}
      </div>
    )
  }

  return (
    <div
      onClick={handleGlobalClick}
      className="screen-fill"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {turnIndicator}
        {renderToggle}
      </div>
      {/* Fixed-height slot so ships sinking mid-game never reflows the boards */}
      <div style={{ height: 14, display: 'flex', alignItems: 'center' }}>
        {sunkDisplay}
      </div>
      {enemyBoard}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <ActionBar compact />
        {myBoard}
      </div>
      <SonarResult />
      {warningDialog}
    </div>
  )
}
